import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useLabels, { useInputValidationLabels } from '../../hooks/useLabels';
import Textarea from '../../ui/textarea/Textarea';
import LoadingIcon from '../../ui/loading-icon/LoadingIcon';
import useTextareaMutations from '../../hooks/useTextareaMutations';
import useInputFocus from '../hooks/useInputFocus';
import useInfo from '../../hooks/useInfo';
import { Icon } from '../../ui/icon/Icon';
import { AdaptiveIconProps, IconStyles, IconTheme } from '../../ui/icon/Icon.types';
import { getClassNames } from '../../helpers/classHelpers';
import { isEventEnter } from '../../helpers/eventHelpers';
import { maxMessageLength, advancedAssistantKey, defaultPersonaKey } from '../../constants/consts';
import { ChatInputModel, ChatInputPropsBase } from './models';
import { isLiteralObject, isString } from '../../helpers/typeHelpers';
import { ChatInputOptions, ChatInputVersionOptions } from './ChatInputOptions';
import { useErrorHandlerContext } from '../../contexts/error-handler/ErrorContext';
import { ErrorHandler } from '../../contexts/error-handler/ErrorHandler';
import { TestIds } from '../../mocks/ids';
import { Icons } from '../../ui/icon/icons/material';
import { ObjectTypes, usePathRouting } from '../../ui/markdown-wrapper/hooks/useActions/useActions';
import { useLayout } from '../../contexts/layout/LayoutContext';
import { TourFileUploadTargets } from '../../features/tours/hooks/useTourFileUploads';
import { useListenToPromptChange } from '../../features/chat/hooks/useMessagesState';
import style from './ChatInput.module.scss';
import { useChat } from '../../contexts/chat/ChatContext';
import { isValidNumber } from '../../helpers/numberHelpers';
import { TranscriptionButton, TranscriptionButtonHandle } from "../transcription-button/TranscriptionButton";
import { isDevOrUat } from '../../helpers/envConfigHelper';
import { useAuth } from '../../contexts/auth/AuthContext';

export type ChatInputProps = ChatInputPropsBase & {
    model: ChatInputModel;
    defaultModel?: Partial<ChatInputModel>;

    /**Should return true if the model is valid, false if invalid without error or a string if invalid with a validation error. The ChatInput validates the length of the message, other props should be validated by the caller */
    validateModel?: (model: ChatInputModel) => boolean | string;

    className?: string;

    placeholder: string;
    inputNote?: string;

    /**Set to true to display the prompt options (llm, persona, etc..)*/
    displayNewChatOptions?: boolean;

    /**Indicates that the provided onSubmitPrompt handler is active. Only provide this value to keep the submitting state active beyond the lifespan of the promise returned from onSubmitPrompt  */
    isSubmitting?: boolean;
    onSubmitPrompt: () => Promise<any>;

    /**When true, the input field will be enabled and it will display the cancel prompt icon */
    isGenerating?: boolean;
    onCancelPrompt?: () => void;

    /**Whenever this value changes, the input will set to focus to itself */
    focusSignal?: boolean | number | string;

    extraInputActions?: AdaptiveIconProps[];
};

const getIconTheme = (documentIdsCount: number, isSidebarVisible: boolean) => {
    if (documentIdsCount <= 0) return isSidebarVisible ? IconTheme.info : IconTheme.inherit;
    return IconTheme.success;
};

const TEMP_TRANSCRIPTION_ENABLED_FOR_USERS = [
    'ayham.fakihani@danfoss.com',
    'peter.trobec@danfoss.com',
    'anders.paulsen@danfoss.com',
    'dpar@danfoss.com',
    'simon.svendsen@danfoss.com',
    'dhanwanti.choudhary@danfoss.com',
    'primoz.petric@danfoss.com'
];
export const CHAT_INPUT_ERROR_ID = 'chat-input-error';
const ChatInput: React.FC<ChatInputProps> = ({ model, defaultModel, extraInputActions, onModelMerge, validateModel: externalValidation, className, placeholder, inputNote, isInputDisabled, isSubmitting: isSubmittingExternal, isGenerating, llmOptions, personaOptions, displayNewChatOptions, onSubmitPrompt, onCancelPrompt, focusSignal }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { llm, message, persona, version, build, temperature, documents } = model || {};
    const resizeSignal = !message;

    const { getPersonaVersion, getLlmFromKey } = useInfo();

    const personaVersion = useMemo(() => getPersonaVersion(persona, version, build), [getPersonaVersion, persona, version, build]);

    // Hook below handles when the currently selected version and build are not found (including that they are not longer amiable.)
    // In this case, we fallback to the latest version/build
    useEffect(() => {
        if ((version && !personaVersion.version) || (build && !personaVersion.revision)) {
            const latest = getPersonaVersion(persona, version, build, true);
            onModelMerge({ version: latest.version?.version, build: latest.revision?.build });
        }
    }, [persona, version, build, personaVersion, getPersonaVersion, onModelMerge]);

    const isDocumentUploadEnabled = personaVersion.revision?.documents?.enabled;

    const { registerError, removeError } = useErrorHandlerContext();
    useTextareaMutations(textareaRef, [resizeSignal]);
    useInputFocus(textareaRef, [focusSignal, llm, persona, temperature]);

    const labels = useLabels();
    const inputValidationLabels = useInputValidationLabels();
    const { isRightSidebarVisible } = useLayout();

    const [isSubmittingInternal, setIsSubmittingInternal] = useState(false);
    const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

    const characterLimit = useMemo(() => {
        return getPersonaVersion(persona, version, build).revision?.characterLimit ?? getLlmFromKey(llm)?.characterLimit ?? maxMessageLength;
    }, [getPersonaVersion, getLlmFromKey, llm, persona, version, build]);

    /**Returns true if valid, false if empty, error message if invalid with a validation error */
    const internalValidation = useCallback((m: ChatInputModel) => {
        if (!m?.message?.trim()) return false;

        const limitReached = m.message.length > characterLimit;
        if (!limitReached) return true;

        return `${inputValidationLabels.messageTooLong} ${m.message.length}/${characterLimit}`;
    }, [characterLimit, inputValidationLabels]);

    /**true if the model is valid, false if invalid without error or a string if invalid with a validation error */
    const modelValidation = useMemo(() => {
        const internal = internalValidation(model);
        if (internal !== true)
            return internal;
        return (externalValidation?.(model) ?? true);
    }, [internalValidation, externalValidation, model]);

    useListenToPromptChange((prompt: string, promptTemplateId: string) => {
        if (!prompt) return;
        onModelMerge({ message: prompt, promptTemplateId });
    });

    const isSubmitting = useMemo(() => isSubmittingInternal || isSubmittingExternal, [isSubmittingInternal, isSubmittingExternal]);
    const isDisabled = useMemo(() => isInputDisabled || isSubmitting, [isInputDisabled, isSubmitting]);
    const isInvalid = useMemo(() => modelValidation !== true, [modelValidation]);
    const preventSend = useMemo(() => isDisabled || isInvalid, [isDisabled, isInvalid]);

    const [isTranscriptionRunning, setTranscriptionRunning] = useState(false);
    const transcriptionRef = useRef<TranscriptionButtonHandle>(null);
    const { user } = useAuth();
    const displayTranscription = useMemo(() => {
        // Show in dev and UAT to all users
        if (isDevOrUat())
            return true;
        if (user?.username && TEMP_TRANSCRIPTION_ENABLED_FOR_USERS.includes(user.username.toLowerCase()))
            return true;
        return false;
    }, [user?.username]);

    const toggleTranscription = useCallback(async () => {
        if (!displayTranscription || isDisabled) return;
        await transcriptionRef.current?.toggle();
    }, [isDisabled, displayTranscription]);

    const { invokePathNavigateToSidePanel } = usePathRouting();

    const onSendClickHandler = useCallback(async () => {
        removeError(CHAT_INPUT_ERROR_ID);

        if (isGenerating && onCancelPrompt) {
            return onCancelPrompt();
        }

        // Stop transcription if running
        await transcriptionRef.current?.stop();

        if (preventSend) return;

        try {
            setIsSubmittingInternal(true);
            await onSubmitPrompt();
        }
        catch (e) {
            let errorHeadline = "";
            let errorDescription: Record<string, string> | undefined = undefined;
            if (isString(e)) errorHeadline = e;
            else if (e instanceof Error) {
                errorHeadline = e.message;
            }
            else if (isLiteralObject(e)) {
                //TODO: Better error extraction is needed here. This assume that the error is an Axios/http error
                let { statusText = "Unknown", status = -1, data, detail } = e as any;
                switch (status) {
                    case 0: {
                        errorHeadline = labels.offlineError;
                        break;
                    }
                    default: {
                        errorHeadline = `(${status}): ${statusText}`;
                        errorDescription = data?.detail ?? detail ?? data;
                        break;
                    }
                }
            }

            registerError({ [CHAT_INPUT_ERROR_ID]: { type: 'modal', headline: errorHeadline, notification: { headline: labels.submitFailed, details: errorDescription } } });
        }
        finally {
            setIsSubmittingInternal(false);
        }

    }, [removeError, isGenerating, onCancelPrompt, preventSend, onSubmitPrompt, registerError, labels.submitFailed, labels.offlineError]);
    const isComposingRef = useRef(false);
    useLayoutEffect(() => {
        const $elm = textareaRef.current;
        if (!$elm) return;

        const handlers: [keyof HTMLElementEventMap, () => void][] = [
            ['compositionstart', () => isComposingRef.current = true],
            ['compositionend', () => isComposingRef.current = false]
        ];

        handlers.forEach(([e, h]) => $elm.addEventListener(e, h));
        return () => handlers.forEach(([e, h]) => $elm.removeEventListener(e, h));
    }, [textareaRef]);

    const onKeyDownHandler = useCallback(async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (isComposingRef.current) return;

        const isEnter = isEventEnter(e);
        if (!isEnter) return;

        const holdsShift = e.shiftKey;
        if (holdsShift) return;

        e.preventDefault();

        const holdsCtrl = e.ctrlKey || e.metaKey;

        if (holdsCtrl)
            await toggleTranscription();
        else
            onSendClickHandler();
    }, [onSendClickHandler, toggleTranscription]);


    const onChangeHandler = useCallback((s: string) => {
        if (s && !s.trim()) return;
        onModelMerge({ message: s });
    }, [onModelMerge]);

    const handleAdvancedSettingsToggle = useCallback(() => {
        setIsAdvancedSettingsOpen(prev => !prev);
    }, []);

    const renderCta = useMemo(() => {
        // TODO: Render fn is possibly not needed - check it out and remove if not needed
        const render = (icon: React.ReactNode, onClick?: () => void, className?: string | false) => (
            <div onClick={onClick} className={getClassNames([className, style['cta-icon']])} >{icon}</div>
        );

        if (isSubmitting) return render(<LoadingIcon className={'df-icon'} />, () => {});

        if (isGenerating) return <Icon.Base
            title={labels.stopSubmitTitle}
            iconName={Icons.stop}
            iconStyle={IconStyles.filled}
            theme={IconTheme.accent}
            onClick={onCancelPrompt}
        />;

        return <Icon.Base
            title={labels.submitTitle}
            testId={TestIds.sendButton}
            iconName={Icons.arrowUpward}
            isDisabled={preventSend || isInvalid}
            onClick={onSendClickHandler}
        />;
    }, [labels, preventSend, isInvalid, isSubmitting, isGenerating, onCancelPrompt, onSendClickHandler]);

    const renderExternal = useMemo(() => {
        return extraInputActions?.map((props, i) => (
            <Icon.Adaptive
                key={i}
                {...props}
                className={getClassNames([style['cta-icon'], style['external-icon']])}
            />
        ));
    }, [extraInputActions]);

    const onDocumentUploadClickHandler = useCallback(() => {
        if (!isDocumentUploadEnabled) return;
        invokePathNavigateToSidePanel('/' + ObjectTypes.documents, true);
    }, [invokePathNavigateToSidePanel, isDocumentUploadEnabled]);

    const { persistent: { addFilesToQueue } } = useChat();
    const handleFilePaste = useCallback((files: File[]) => {
        if (!isDocumentUploadEnabled)
            return;
        onDocumentUploadClickHandler();
        addFilesToQueue(files);
    }, [onDocumentUploadClickHandler, isDocumentUploadEnabled, addFilesToQueue]);

    const renderCuf = useMemo(() => {
        if (!isDocumentUploadEnabled) return;

        const documentLength = documents?.length || 0;
        const theme = getIconTheme(documentLength, isRightSidebarVisible);

        return (
            <div className={style['upload-icon-wrapper']} id={TourFileUploadTargets.icon}>
                <Icon.Base
                    title={labels.uploadDocumentTitle}
                    testId={TestIds.uploadDocButton}
                    iconName={Icons.uploadDocument}
                    dataAttributes={{ 'data-documents-quantity': documentLength > 9 ? '9+' : documentLength.toString() ?? '' }}
                    theme={theme}
                    className={getClassNames([style['cta-icon']])}
                    onClick={onDocumentUploadClickHandler}
                />
            </div>
        );
    }, [documents?.length, onDocumentUploadClickHandler, isDocumentUploadEnabled, isRightSidebarVisible, labels.uploadDocumentTitle]);

    const renderQuickPrompt = useMemo(() => {
        return (

            <Icon.Base
                title={labels.promptListOpenQuickPrompts}
                testId={TestIds.openQuickPromptButton}
                iconName={Icons.promptSuggestion}
                className={getClassNames([style['cta-icon']])}
                onClick={() => invokePathNavigateToSidePanel('/' + ObjectTypes.prompts, true)}
            />

        );
    }, [invokePathNavigateToSidePanel, labels.promptListOpenQuickPrompts]);

    const renderAdvancedSettingsToggle = useMemo(() => {
        if (!displayNewChatOptions || persona !== defaultPersonaKey) return null;

        return (
            <div className={getClassNames([style['settings-toggle-container']])} onClick={handleAdvancedSettingsToggle}>
                <Icon.Base
                    title={labels.basicSettings}
                    testId="advanced-settings-toggle"
                    iconName={Icons.settings}
                    theme={isAdvancedSettingsOpen ? IconTheme.info : IconTheme.inherit}
                    className={getClassNames([style['cta-icon']])}
                />
                <span className={getClassNames([style['settings-toggle-text']])}>{labels.basicSettings}</span>
            </div>
        );
    }, [displayNewChatOptions, persona, labels.basicSettings, handleAdvancedSettingsToggle, isAdvancedSettingsOpen]);
    const renderAdvancedOptions = useMemo(() => {
        if (isAdvancedSettingsOpen) return null;
        if (!displayNewChatOptions || persona !== defaultPersonaKey) return null;
        const overrides: ({ key: keyof ChatInputModel, label?: string, value: string; })[] = [];
        if (model.llm && model.llm !== defaultModel?.llm) overrides.push({ key: "llm", value: llmOptions.find(option => option.value === model.llm)?.label || model.llm });
        if (isValidNumber(model.temperature) && model.temperature !== defaultModel?.temperature) overrides.push({ key: "temperature", label: labels.temperature, value: model.temperature.toString() });

        if (overrides.length === 0) return null;
        const resetValue = (key: keyof ChatInputModel) => {
            onModelMerge({ [key]: undefined });
        };
        return <div className={style['advanced-options-preview']}>
            {
                overrides.map(({ key, label, value }) => (
                    <span key={key} className={style['advanced-option-item']}>
                        <span className={style['advanced-option-remove']} onClick={() => resetValue(key)}><Icon.Base iconName={Icons.clear} title='' theme={IconTheme.error} /></span>
                        {label && <span className={style['advanced-option-key']}>{label}:</span>}
                        <span className={style['advanced-option-value']}>{value}</span>
                    </span>
                ))
            }
        </div>;
    }, [displayNewChatOptions, isAdvancedSettingsOpen, persona, defaultModel?.llm, defaultModel?.temperature, model.llm, model.temperature, labels, llmOptions, onModelMerge]);

    const isAdvancedAssistant = useMemo(() => persona === advancedAssistantKey, [persona]);

    return (
        <div data-testid={TestIds.chatInputContainer} className={getClassNames([className, style['chat-input']])}>
            <div className={style['input-with-settings']}>
                <div className={style['input-section']}>
                    {displayNewChatOptions && <ChatInputVersionOptions persona={persona} version={version} build={build} onModelMerge={onModelMerge} personaOptions={personaOptions} llmOptions={llmOptions} isInputDisabled={isDisabled} />}

                    {!isAdvancedAssistant && (
                        <div className={getClassNames([style['input-container-wrapper']])}>
                            <div id={TourFileUploadTargets.submit} className={getClassNames([style.container, 'df-input-container'])}>
                                <Textarea
                                    testId={TestIds.chatInput}
                                    ref={textareaRef}
                                    className={getClassNames([style.textarea, isTranscriptionRunning && style.transcribing])}
                                    value={message || ''}
                                    placeholder={placeholder}
                                    isInvalid={isString(modelValidation)}
                                    isDisabled={isDisabled}
                                    onChange={onChangeHandler}
                                    onKeyDown={onKeyDownHandler}
                                    maxLength={characterLimit + 100} // +100 - Needed for allowing users to exceed the limit so the error message can be displayed
                                    //Disabling paste and drop feature of textarea input until it's fully tested
                                    onFilesPaste={isDocumentUploadEnabled ? handleFilePaste : undefined}
                                />
                            </div>
                            <div className={getClassNames([style['cta-icon-wrapper']])}>
                                {renderAdvancedSettingsToggle}
                                {renderAdvancedOptions}
                                <div className={style['action-icons-group']}>
                                    {renderExternal}
                                    {renderCuf}
                                    {renderQuickPrompt}
                                    {displayTranscription && <TranscriptionButton
                                        ref={transcriptionRef}
                                        textareaRef={textareaRef}
                                        message={message || ""}
                                        onMessageChange={(newMessage) => onModelMerge({ message: newMessage })}
                                        onRunningChange={(isRunning) => setTranscriptionRunning(isRunning)}
                                        isDisabled={isDisabled}
                                        startTitle={labels.startVoiceInput}
                                        stopTitle={labels.stopVoiceInput}
                                    />
                                    }
                                    {renderCta}
                                </div>
                            </div>
                        </div>

                    )}
                </div>
            </div>

            {isString(modelValidation) && <label className='df-helper-text df-color-error'>{modelValidation}</label>}
            {inputNote && <label className={getClassNames([style['disclaimer-note'], 'df-helper-text'])}>{inputNote}</label>}

            {displayNewChatOptions && isAdvancedSettingsOpen && persona === defaultPersonaKey && <div className={style['chat-input-options-wrapper']}>
                <ChatInputOptions
                    llm={llm || defaultModel?.llm}
                    temperature={temperature ?? defaultModel?.temperature}
                    persona={persona}
                    version={version}
                    build={build}
                    onModelMerge={onModelMerge}
                    llmOptions={llmOptions}
                    personaOptions={personaOptions}
                    isInputDisabled={isDisabled}
                />
            </div>}

            <ErrorHandler.Modal id={CHAT_INPUT_ERROR_ID} />
        </div>
    );
};

export default ChatInput;
